import logger from "utils/logger";

/**
 * Typed wrapper around window.localStorage (P10.1, RM-3100).
 *
 * - Keys are a closed union so typos fail at compile time instead of
 *   silently returning null at runtime.
 * - Every call is guarded: localStorage can throw (Safari private mode,
 *   blocked site data, full quota) and none of our call sites can do
 *   anything useful with that failure beyond degrading to "no value".
 */
export type StorageKey =
  | "access-token"
  | "client"
  | "uid"
  | "currentAccount"
  | "trusted-device-token"
  | "passkey-hint";

// Survives clear(): the "remember this device" token from the sign-in code
// step (RM-3849) and the "this browser uses a passkey" hint (RM-3845) belong
// to the browser, not to the signed-in session, so signing out or starting a
// new sign-in must not forget them.
const KEPT_ON_CLEAR: StorageKey[] = ["trusted-device-token", "passkey-hint"];

const storage = {
  get(key: StorageKey): string | null {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      logger.error(err);
      return null;
    }
  },

  set(key: StorageKey, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      logger.error(err);
    }
  },

  remove(key: StorageKey): void {
    try {
      localStorage.removeItem(key);
    } catch (err) {
      logger.error(err);
    }
  },

  clear(): void {
    try {
      const kept = KEPT_ON_CLEAR.map((key) => [key, localStorage.getItem(key)] as const);
      localStorage.clear();
      kept.forEach(([key, value]) => {
        if (value !== null) localStorage.setItem(key, value);
      });
    } catch (err) {
      logger.error(err);
    }
  },
};

export default storage;
